import React, { useState } from 'react';

import { List, Datagrid, TextField, DeleteButton, Edit, TextInput, Button, Create, SimpleForm, TopToolbar, FormTab, TabbedForm, required, BooleanInput, Toolbar, SaveButton, FunctionField, BooleanField, CreateButton, useNotify, Confirm, useDataProvider, useRefresh } from 'react-admin';
import LaunchIcon from '@material-ui/icons/Launch';
import DeleteIcon from '@material-ui/icons/Delete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@material-ui/core/styles';

import { useTranslate } from '../common/language';
import { validateMaxLength } from './../components/Validator';
import { araTiquetsUrl } from '../common/constants';
import { EventsTopicsItemsList } from './events_topics_items';
import RichTextInput from 'ra-input-rich-text';

const isSpecialTopic = record => record && ['featured', 'hidden'].includes(record.friendly_url);
const topicPublicUrl = record => `${araTiquetsUrl}/temes/${record.friendly_url}`;
const descriptionToolbar = [
  [{ header: [2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  ['clean'],
];

const normalizeImageSize = value => {
  const trimmed = value ? value.trim() : '';
  if (!trimmed || trimmed.toLowerCase() === 'auto') return false;
  return trimmed;
};

const formatImage = (quill, imageNode, width, height) => {
  const blot = quill.constructor.find(imageNode);
  if (!blot) return;

  const index = blot.offset(quill.scroll);
  quill.formatText(index, 1, 'width', width);
  quill.formatText(index, 1, 'height', height);
  quill.setSelection(index + 1, 0);
};

const promptImageSize = (quill, imageNode, translate) => {
  const currentWidth = imageNode.getAttribute('width') || '';
  const currentHeight = imageNode.getAttribute('height') || '';
  const widthInput = window.prompt(translate('events_topics.image_width_prompt'), currentWidth);
  if (widthInput === null) return;

  const heightInput = window.prompt(translate('events_topics.image_height_prompt'), currentHeight);
  if (heightInput === null) return;
  const width = normalizeImageSize(widthInput);
  const height = normalizeImageSize(heightInput);

  formatImage(quill, imageNode, width, height);
};

const configureDescriptionQuill = translate => quill => {
  quill.root.addEventListener('click', event => {
    if (event.target && event.target.tagName === 'IMG') {
      promptImageSize(quill, event.target, translate);
    }
  });
};

const PublicLinkField = ({ record }) => {
  if (!record || !record.friendly_url) return null;

  const publicUrl = topicPublicUrl(record);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>{publicUrl}</span>
      <Button
        component="a"
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={event => event.stopPropagation()}
      >
        <LaunchIcon />
      </Button>
    </div>
  );
};

export const EventsTopicsList = props => {
  const translate = useTranslate();

  return (
    <>
      <Typography variant="h5" style={{ marginTop: 24, marginBottom: 16, color: '#666' }}>
        {translate('events_topics.title')}
      </Typography>
      <List
        {...props}
        actions={<ListActions />}
        bulkActionButtons={false}
        perPage={1000}
        pagination={false}
        sort={{ field: 'id', order: 'ASC' }}
        title={translate('events_topics.title')}
      >
        <Datagrid rowClick="edit">
          <TextField source="name" label={translate('events_topics.name')} sortable={false} />
          <FunctionField label={translate('events_topics.url')} render={record => <PublicLinkField record={record} />} sortable={false} />
          <BooleanField source="actived" label={translate('events_topics.actived')} sortable={false} />
          <FunctionField label="" render={record => <TopicDeleteButton record={record} />} sortable={false} />
        </Datagrid>
      </List>
    </>
  );
};

const TopicDeleteButton = ({ record }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  if (isSpecialTopic(record)) return null;

  const handleClick = event => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = event => {
    if (event) event.stopPropagation();
    setOpen(false);
  };

  const handleConfirm = event => {
    if (event) event.stopPropagation();
    setLoading(true);
    dataProvider.delete('events_topics', { id: record.id, previousData: record })
      .then(() => {
        notify(translate('events_topics.delete_success'));
        setOpen(false);
        refresh();
      })
      .catch(error => {
        notify(error.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Button label="" onClick={handleClick} className="red_button">
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={loading}
        title={translate('events_topics.delete_title')}
        content={translate('events_topics.delete_content')}
        onConfirm={handleConfirm}
        onClose={handleClose}
      />
    </>
  );
};

const ListActions = props => {
  const translate = useTranslate();

  return (
    <TopToolbar>
      <CreateButton />
      <Button component="a" href={`${araTiquetsUrl}/temes`} target="_blank" rel="noopener noreferrer" label={translate('main_menu.ara_tiquets')}>
        <LaunchIcon />
      </Button>
    </TopToolbar>
  );
};

const MyActions = ({ data }) => {
  const translate = useTranslate();

  return (
    <TopToolbar>
      {data && data.friendly_url ? (
        <Button component="a" href={topicPublicUrl(data)} target="_blank" rel="noopener noreferrer" label={translate('events_topics.public_page')}>
          <LaunchIcon />
        </Button>
      ) : null}
    </TopToolbar>
  );
};

export const EventsTopicsEdit = ({ id, ...props }) => {
  const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();

  const onFailure = error => {
    notify(error.message, 'error');
  };

  return (
    <Edit
      title={<ElementTitle {...props} />}
      {...props}
      id={id}
      mutationMode="pessimistic"
      actions={<MyActions {...props} />}
      onFailure={onFailure}
    >
      <TabbedForm toolbar={<FormToolbar />} keepDirtyOnReinitialize>
        <FormTab label={translate('classrooms.data')} contentClassName={classes.tab} path="">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextInput fullWidth source="name" label={translate('events_topics.name')} validate={[required(), validateMaxLength(255, translate('common.validation.maxLength'))]} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextInput fullWidth source="friendly_url" label={translate('events_topics.friendly_url')} validate={[required(), validateMaxLength(255, translate('common.validation.maxLength'))]} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <BooleanInput fullWidth source="actived" label={translate('events_topics.actived')} />
            </Grid>
            <Grid item xs={12}>
              <RichTextInput fullWidth source="description" label={translate('events_topics.description')} toolbar={descriptionToolbar} configureQuill={configureDescriptionQuill(translate)} />
            </Grid>
          </Grid>
        </FormTab>

        <FormTab label={translate('events_topics_items.title')} contentClassName={classes.tab} path="events">
          <EventsTopicsItemsList events_topics_id={id} />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};

const ElementTitle = ({ record }) => {
  const translate = useTranslate();
  return <span>{translate('common.edit')} {record ? `"${record.name}"` : ''}</span>;
};

const FormToolbar = props => {
  const record = props.record;

  return (
    <Toolbar {...props} className="user_form_toolbar">
      <SaveButton />
      {!isSpecialTopic(record) ? <DeleteButton redirect="/events_topics" mutationMode="pessimistic" /> : null}
    </Toolbar>
  );
};

const useStyles = makeStyles({
  tab: {
    maxWidth: '100%',
    display: 'block',
  },
});

export const EventsTopicsCreate = props => {
  const translate = useTranslate();

  return (
    <Create {...props}>
      <SimpleForm redirect="list" initialValues={{ actived: true }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" className="grey">{translate('events_topics.create')}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput fullWidth source="name" label={translate('events_topics.name')} validate={[required(), validateMaxLength(255, translate('common.validation.maxLength'))]} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput fullWidth source="friendly_url" label={translate('events_topics.friendly_url')} validate={[required(), validateMaxLength(255, translate('common.validation.maxLength'))]} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <BooleanInput fullWidth source="actived" label={translate('events_topics.actived')} />
          </Grid>
          <Grid item xs={12}>
            <RichTextInput fullWidth source="description" label={translate('events_topics.description')} toolbar={descriptionToolbar} configureQuill={configureDescriptionQuill(translate)} />
          </Grid>
        </Grid>
      </SimpleForm>
    </Create>
  );
};
